import { supabase } from '@/integrations/supabase/client';

// Type-safe analytics service that works with current database schema
type AnalyticsInsert = {
  content_type?: string;
  content_id?: string;
  event_type?: string;
  user_id?: string;
  metadata?: Record<string, any>;
};

export interface ContentAnalytics {
  id: string;
  content_type: 'interview' | 'comment' | 'debate';
  content_id: string;
  event_type: 'view' | 'like' | 'share' | 'comment' | 'search';
  user_id?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface UserAnalytics {
  id: string;
  user_id?: string;
  session_id: string;
  page_path: string;
  action: string;
  duration_seconds?: number;
  device_info?: Record<string, any>;
  created_at: string;
}

export interface PublishingAnalytics {
  id: string;
  interview_id: string;
  action: 'published' | 'unpublished' | 'rejected' | 'queued';
  user_id?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface MetricsSummary {
  id: string;
  metric_type: string;
  metric_value: number;
  period_start: string;
  period_end: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface AnalyticsOverview {
  totalViews: number;
  totalInterviews: number;
  totalComments: number;
  totalDebates: number;
  dailyViews: Array<{ date: string; views: number }>;
  topContent: Array<{ title: string; views: number; type: string }>;
  userEngagement: {
    averageSessionDuration: number;
    bounceRate: number;
    pagesPerSession: number;
  };
}

export class AnalyticsService {
  // Track content analytics (simplified for current setup)
  static async trackContentEvent(
    contentType: 'interview' | 'comment' | 'debate',
    contentId: string,
    eventType: 'view' | 'like' | 'share' | 'comment' | 'search',
    userId?: string,
    metadata?: Record<string, any>
  ) {
    try {
      // For now, we'll track analytics in a simplified way using existing tables
      console.log('Analytics tracking:', { contentType, contentId, eventType, userId, metadata });
      
      // We can store analytics in the metadata of existing tables for now
      if (contentType === 'interview') {
        const { data: interview } = await supabase
          .from('interviews')
          .select('id')
          .eq('id', contentId)
          .single();
        
        if (interview) {
          // Track the event in console for now
          console.log(`Tracked ${eventType} on ${contentType} ${contentId}`);
        }
      }
    } catch (error) {
      console.error('Error tracking content event:', error);
    }
  }

  // Track user analytics (simplified)
  static async trackUserActivity(
    sessionId: string,
    pagePath: string,
    action: string,
    userId?: string,
    durationSeconds?: number,
    deviceInfo?: Record<string, any>
  ) {
    try {
      console.log('User activity:', { sessionId, pagePath, action, userId, durationSeconds, deviceInfo });
    } catch (error) {
      console.error('Error tracking user activity:', error);
    }
  }

  // Track publishing analytics (simplified)
  static async trackPublishingEvent(
    interviewId: string,
    action: 'published' | 'unpublished' | 'rejected' | 'queued',
    userId?: string,
    metadata?: Record<string, any>
  ) {
    try {
      console.log('Publishing event:', { interviewId, action, userId, metadata });
    } catch (error) {
      console.error('Error tracking publishing event:', error);
    }
  }

  // Get analytics overview (using existing data)
  static async getAnalyticsOverview(days: number = 30): Promise<AnalyticsOverview> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get metrics from existing tables
      const [interviewsResult, commentsResult, debatesResult] = await Promise.all([
        supabase
          .from('interviews')
          .select('id, created_at, title')
          .eq('publication_status', 'published'),
        supabase
          .from('comments')
          .select('id, created_at'),
        supabase
          .from('debates')
          .select('id, created_at')
      ]);

      // Mock daily views based on interview creation dates
      const interviews = interviewsResult.data || [];
      const dailyViews = this.generateDailyViewsMock(interviews, days);

      // Create top content from interviews
      const topContent = interviews.slice(0, 10).map((interview, index) => ({
        title: interview.title || `Interview ${index + 1}`,
        views: Math.floor(Math.random() * 1000) + 100,
        type: 'interview'
      }));

      return {
        totalViews: interviews.length * 15, // Mock: assume 15 views per interview
        totalInterviews: interviews.length,
        totalComments: commentsResult.data?.length || 0,
        totalDebates: debatesResult.data?.length || 0,
        dailyViews,
        topContent,
        userEngagement: {
          averageSessionDuration: 4.5, // Mock values
          bounceRate: 35,
          pagesPerSession: 2.8
        }
      };
    } catch (error) {
      console.error('Error getting analytics overview:', error);
      return {
        totalViews: 0,
        totalInterviews: 0,
        totalComments: 0,
        totalDebates: 0,
        dailyViews: [],
        topContent: [],
        userEngagement: {
          averageSessionDuration: 0,
          bounceRate: 0,
          pagesPerSession: 0
        }
      };
    }
  }

  // Helper method to generate mock daily views
  private static generateDailyViewsMock(interviews: any[], days: number) {
    const dailyViews = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Mock views based on interviews created that day
      const interviewsOnDay = interviews.filter(interview => {
        const interviewDate = new Date(interview.created_at);
        return interviewDate.toDateString() === date.toDateString();
      }).length;
      
      dailyViews.push({
        date: date.toISOString().split('T')[0],
        views: interviewsOnDay * 10 + Math.floor(Math.random() * 50) + 20
      });
    }
    
    return dailyViews;
  }

  // Get content performance (mock data for now)
  static async getContentPerformance(contentId: string, contentType: string) {
    try {
      // Mock performance data based on content type
      const mockViews = Math.floor(Math.random() * 500) + 50;
      const mockLikes = Math.floor(Math.random() * 50) + 5;
      const mockShares = Math.floor(Math.random() * 20) + 2;
      const mockComments = Math.floor(Math.random() * 30) + 3;

      return {
        views: mockViews,
        likes: mockLikes,
        shares: mockShares,
        comments: mockComments,
        totalEngagement: mockLikes + mockShares + mockComments,
        rawData: []
      };
    } catch (error) {
      console.error('Error getting content performance:', error);
      return {
        views: 0,
        likes: 0,
        shares: 0,
        comments: 0,
        totalEngagement: 0,
        rawData: []
      };
    }
  }

  // Get publishing metrics (based on existing interview data)
  static async getPublishingMetrics(days: number = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: interviews, error } = await supabase
        .from('interviews')
        .select('publication_status, created_at')
        .gte('created_at', startDate.toISOString());

      if (error) throw error;

      const published = interviews?.filter(item => item.publication_status === 'published').length || 0;
      const pending = interviews?.filter(item => item.publication_status === 'pending').length || 0;
      const rejected = interviews?.filter(item => item.publication_status === 'rejected').length || 0;

      const total = published + pending + rejected;

      return {
        published,
        rejected,
        queued: pending,
        total,
        publishRate: total > 0 ? (published / total) * 100 : 0
      };
    } catch (error) {
      console.error('Error getting publishing metrics:', error);
      return {
        published: 0,
        rejected: 0,
        queued: 0,
        total: 0,
        publishRate: 0
      };
    }
  }
}